import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { useCallback, useMemo, useRef, useState } from 'react';
import { isLoading } from 'reference-resolvers-lite/Status';
import { createStandardCachedResolver } from 'reference-resolvers-lite/utils/createStandardCachedResolver';
import { useHttpClient } from '../client/HttpClientContext';
import { FILE_ID_OR_URL } from 'customer-data-objects/externalOptions/ExternalOptionsReferenceTypes';
import { buildPendingValidationState } from '../validation/hooks/usePropertyValidation';
import { uploadFiles } from '../files/FilesApi';
import AddFileButton from '../files/AddFileButton';
import { parseMultiEnumValue } from 'customer-data-property-utils/parseMultiEnumValue';
import { addTrailingSemiColons, computeProgress, getFileReferencesByIds, getFileVisibilityForProperty, getMissingReferences, getNewValue, getValidationState, removeTrailingSemiColons } from '../files/utils';
import { MAX_UPLOAD_SIZE_EXCEEDED } from '../files/constants';
import FileValues from '../files/values/FileValues';
import FileDrafts from '../files/drafts/FileDrafts';
import Raven from 'raven-js';
import useFetchBatchResolverWithReload, { isReloading } from '../files/hooks/useFetchBatchResolverWithReload';
import PropertyInputFilesWrapper from './PropertyInputFilesWrapper';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { usePropertyExtensionsMetadata } from '../v2/hooks/usePropertyExtensionsMetadata';
import useComponentMountMetric from '../metrics/useComponentMountMetric';
import { useObjectTypeId } from '../v2/hooks/useObjectTypeId';
import { useProperty } from '../v2/hooks/useProperty';
import { usePropertyValue } from '../v2/hooks/usePropertyValue';
import { syntheticEvent } from 'foundations-components/transitional/utils/SyntheticEvent';
import { RESOLVER_DEBOUNCE_KEY, TIME_TOKENS } from '../utils/timeTokens';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const PropertyInputFilesV2 = props => {
  useComponentMountMetric('PropertyInputFilesV2');
  const [fileDraftsMap, setFileDraftsMap] = useState(() => new Map());
  const [allUploadsFailed, setAllUploadsFailed] = useState(false);
  const fileDraftsErrorsMapRef = useRef(new Map());
  const httpClient = useHttpClient();
  const objectTypeId = useObjectTypeId();
  const property = useProperty();
  const originalValue = usePropertyValue();
  const {
    onValidationChange,
    onChange,
    readOnly,
    disabled,
    formControlUse
  } = props;
  const {
    loading: propertyExtensionsMetadataLoading,
    data: propertyExtensionsMetadata,
    error: propertyExtensionsMetadataError
    // TODO - handle error state - disable file uploads on error
    // https://git.hubteam.com/HubSpot/crm-property-management-fe-issues/issues/433
  } = usePropertyExtensionsMetadata(objectTypeId, {
    metricsTrackingSource: 'PropertyInputFilesV2'
  });
  const fileVisibility = getFileVisibilityForProperty(propertyExtensionsMetadata, property.name);

  // All Property Inputs return a new value as a part of onValidationChange callback.
  // Few host apps that depend on this behavior to update their draft value and validation state
  // In case of file properties, errored files do not get a file id. When an error occurs, we trigger an invalid state with existing value.
  // When the error is cleared out, we again trigger a valid state with the same value. This leads to issues in few host apps.

  // It is unsafe include file name in the value. Semicolons are safe because parseMultienumValue ignores them.
  // Hence we are adding trailing semicolons for the number of errored files. onValidationChange is always called with an updated value
  const value = removeTrailingSemiColons(originalValue);

  // we cannot use resolver from getPropertyComponentResolver because it could be undefined.
  // instead of asserting resolver while calling useFetchBatchResolver, we create a new resolver here.
  // reference-resolver-lite takes care of caching. So we don't need to worry about creating multiple resolvers.
  const resolver = createStandardCachedResolver(FILE_ID_OR_URL, objectTypeId, {
    httpClient,
    debouncePeriod: TIME_TOKENS[RESOLVER_DEBOUNCE_KEY]
  });
  const fileIds = parseMultiEnumValue(value);
  const {
    references: fileReferences,
    status
  } = useFetchBatchResolverWithReload(resolver, fileIds);
  const missingReferences = useMemo(() => getMissingReferences(fileReferences, fileIds), [fileIds, fileReferences]);

  // covers against race conditions
  // when files are removed while previous file references are still being fetched, we see the old values coming back
  // this function also reorders references in the order of fileIds
  // as references are not refetched on order changes
  const fileReferencesByIds = useMemo(() => getFileReferencesByIds(fileReferences, fileIds), [fileIds, fileReferences]);
  const handleChange = useCallback(currentValue => {
    const newValue = addTrailingSemiColons(currentValue, fileDraftsErrorsMapRef.current);
    onChange(syntheticEvent(String(newValue)));
    onValidationChange(getValidationState(fileDraftsErrorsMapRef.current, property, newValue));
  }, [onChange, onValidationChange, property]);
  const progressCallback = useCallback((e, file) => {
    setFileDraftsMap(prev => new Map(prev).set(file, {
      progress: computeProgress(e)
    }));
  }, []);
  const successCallback = useCallback((file, fileReference) => {
    setFileDraftsMap(prev => new Map(prev).set(file, {
      progress: 100,
      fileReference
    }));
  }, []);
  const errorCallback = useCallback((file, errorType) => {
    fileDraftsErrorsMapRef.current.set(file, {
      errorType
    });
    setFileDraftsMap(prev => new Map([...prev].filter(([key]) => key !== file)));
  }, []);
  const handleErrorListRemove = useCallback(file => {
    fileDraftsErrorsMapRef.current.delete(file);
    handleChange(value);
  }, [handleChange, value]);
  const handleFileAction = useCallback(({
    action,
    fileId
  }) => {
    const newValue = getNewValue({
      currentValue: value,
      missingReferences,
      action,
      fileId
    });
    handleChange(newValue);
  }, [handleChange, value, missingReferences]);
  const handleSelection = useCallback((filesBelowMaxSizeLimit, filesAboveMaxSizeLimit) => {
    filesAboveMaxSizeLimit.forEach(file => fileDraftsErrorsMapRef.current.set(file, {
      errorType: MAX_UPLOAD_SIZE_EXCEEDED
    }));
    if (filesBelowMaxSizeLimit.length === 0) {
      handleChange(value);
      return;
    }

    // We want to prevent save when file uploads are in progress
    // Reusing the pending validation state to prevent save
    onValidationChange(buildPendingValidationState(property, value));
    setFileDraftsMap(new Map(filesBelowMaxSizeLimit.map(file => [file, {
      progress: 0
    }])));
    setAllUploadsFailed(false);
    uploadFiles(filesBelowMaxSizeLimit, httpClient, progressCallback, successCallback, errorCallback, fileVisibility, property.dataSensitivity).then(uploadedFileIds => {
      const newValue = value ? `${value};${uploadedFileIds}` : uploadedFileIds;
      handleChange(newValue);
    }).catch(err => {
      Raven.captureException(err, {
        extra: {
          value
        }
      });
      setAllUploadsFailed(true);
      handleChange(value);
    }).finally(() => {
      setFileDraftsMap(new Map());
    });
  }, [httpClient, onValidationChange, progressCallback, successCallback, errorCallback, handleChange, property, value, fileVisibility]);
  if (!isReloading(status) && isLoading(status) || propertyExtensionsMetadataLoading) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      size: "small"
    });
  }
  const isEditDisabled = readOnly || disabled || fileDraftsMap.size > 0;
  const currentFilesCount = fileIds.length + fileDraftsMap.size + fileDraftsErrorsMapRef.current.size;
  const Input = /*#__PURE__*/_jsxs(TFlex, {
    gap: 8,
    direction: "column",
    children: [/*#__PURE__*/_jsxs("div", {
      style: {
        minWidth: '100%'
      },
      children: [/*#__PURE__*/_jsx(FileValues, {
        fileReferences: fileReferencesByIds,
        missingReferences: missingReferences,
        isEditDisabled: isEditDisabled,
        onFileAction: handleFileAction,
        uploadsInProgress: fileDraftsMap.size > 0,
        status: status
      }), /*#__PURE__*/_jsx(FileDrafts, {
        fileDraftsMap: fileDraftsMap,
        fileDraftsErrorsMap: fileDraftsErrorsMapRef.current,
        onRemove: handleErrorListRemove,
        allUploadsFailed: allUploadsFailed
      })]
    }), /*#__PURE__*/_jsx(AddFileButton, {
      isEditDisabled: isEditDisabled,
      hasPropertyExtensionsMetadataError: !!propertyExtensionsMetadataError,
      onSelect: handleSelection,
      currentFilesCount: currentFilesCount
    })]
  });
  if (formControlUse === 'table-cell') {
    return /*#__PURE__*/_jsx(PropertyInputFilesWrapper, Object.assign({
      input: Input,
      fileReferences: fileReferencesByIds
    }, props));
  }
  return Input;
};
export default PropertyInputFilesV2;